import { CreateCommentDocument } from '~/api/graphql/generated'
import type { CreateCommentMutation, CreateCommentMutationVariables } from '~/api/graphql/generated'
import { reset, FormKitNode } from '@formkit/core'
import { useMutation } from '@vue/apollo-composable'
import { useRecaptcha } from '~/api/drupal/useRecaptcha'

const getErrorMessagesFrom = (list?: Array<{ message?: string | null } | string | null> | null) =>
  list
    ?.filter(Boolean)
    ?.map((item) => (typeof item === 'string' ? item : item?.message))
    .filter(Boolean) || []

export const useCommentForm = () => {
  const { t } = useI18n()
  const store = useCommentFormStore()
  const { generateToken, verifyToken } = useRecaptcha()

  const { mutate, error } = useMutation<CreateCommentMutation, CreateCommentMutationVariables>(CreateCommentDocument)

  const postId = computed(() => store.postId)
  const parent = computed(() => ({
    default: store.parent.default,
    current: store.parent.current,
    id: store.parent.id,
  }))
  const errors = ref<string[]>([])
  const form = ref<FormKitNode | undefined>()
  const showSuccessMessage = ref(false)

  const onSubmit = async (formData: { name: string; email: string; body: string }) => {
    if (!postId.value) {
      errors.value = [t('form.errors.generalError')]
      console.warn('no entity id provided')
      return
    }

    const token = await generateToken('commentFormRecaptcha')
    if (!token) {
      errors.value = [t('form.errors.generalError')]
      return
    }

    const recaptchaResult = await verifyToken(token)
    if (!recaptchaResult) {
      errors.value = [t('form.errors.generalError')]
      return
    }

    showSuccessMessage.value = false

    const data = {
      ...formData,
      entityId: postId.value,
      parentId: parent.value.id,
    }
    const result = await mutate(data)

    if (
      result &&
      result.data &&
      result.data.createComment?.errors?.length === 0 &&
      result.data.createComment?.violations?.length === 0
    ) {
      showSuccessMessage.value = true
    } else {
      errors.value = [
        t('form.errors.generalError'),
        ...getErrorMessagesFrom(result?.data?.createComment?.errors),
        ...getErrorMessagesFrom(result?.data?.createComment?.violations),
      ]
    }
  }

  const onSuccessAcknowledgement = () => {
    showSuccessMessage.value = false
    store.setParent(null)
    reset(form.value || '')
  }

  watch(
    () => error.value,
    () => {
      if (error.value) {
        errors.value = [error.value.message]
      }
    },
  )

  return {
    postId,
    parent,
    form,
    showSuccessMessage,
    errors,
    onSubmit,
    onSuccessAcknowledgement,
    setPostId: store.setPostId,
    setParent: store.setParent,
  }
}
